import { useEffect, useState, type ReactNode } from 'react';
import { Navigate, useLocation, useParams } from 'react-router-dom';

import { canonicalCrmHref, isCrmId } from '../../../supabase/functions/_shared/crmFiche';
import { crmHref, RETURN_PARAM } from '../../lib/bureau/crmLinks';
import { logWarn } from '../../lib/logger';
import { supabase } from '../../lib/supabase';

/**
 * The CRM's older account routes, still named by links written before the fiches (Otto's chat,
 * notifications, mails): each opens the account's fiche, the Comptes page selected by its id
 * (PLAN-CRM-ACCOUNT-SURFACE, `_shared/crmFiche.ts`). Gilles, 12/10/2026: a link from Otto opened
 * « old fiches I thought were deleted ».
 */

/** `/admin/crm/accounts/<id>`: the fiche, where it used to land on the list's first account. */
export function LegacyAccountRoute() {
  const { pathname, search } = useLocation();
  const href = `${pathname}${search}`;
  const fiche = canonicalCrmHref(href);
  // Not an account's id (a typo, a trailing slash): the list, as before.
  return <Navigate to={fiche === href ? '/admin/crm/accounts' : fiche} replace />;
}

/**
 * `/admin/crm/company/<id>`: the tabbed page of a LEAD (`children`). An account's id opens its fiche,
 * decided before the page mounts, so the old page never shows for an account; the way back to the
 * Bureau (`retour`) goes along. A lead comes first, as on the page: the account created from a lead
 * carries the lead's id (trigger `sync_lead_to_account`), and that id must keep opening the lead.
 * If either cannot be read, the page decides, as before.
 */
export function CompanyRoute({ children }: { children: ReactNode }) {
  const { id } = useParams<{ id: string }>();
  const { search } = useLocation();
  const candidate = isCrmId(id) ? id : null;
  const [account, setAccount] = useState<{ id: string; isAccount: boolean } | null>(null);

  useEffect(() => {
    if (!candidate) return;
    let alive = true;
    void Promise.all([
      supabase.from('onboarding_leads').select('id').eq('id', candidate).maybeSingle(),
      supabase.from('crm_accounts').select('id').eq('id', candidate).maybeSingle(),
    ]).then(([lead, accountRow]) => {
      if (!alive) return;
      const error = lead.error ?? accountRow.error;
      if (error) logWarn('crm', 'company route: lead or account unreadable, the page decides', { id: candidate, error: error.message });
      setAccount({ id: candidate, isAccount: !error && !lead.data && Boolean(accountRow.data) });
    });
    return () => {
      alive = false;
    };
  }, [candidate]);

  if (!candidate) return <>{children}</>;
  if (account?.id !== candidate) {
    return <div className="h-40 animate-pulse rounded-2xl bg-neutral-100 dark:bg-neutral-800" aria-busy="true" />;
  }
  if (account.isAccount) {
    return <Navigate to={crmHref('account', candidate, new URLSearchParams(search).get(RETURN_PARAM))} replace />;
  }
  return <>{children}</>;
}
